import { filter, uniqBy, find } from 'lodash';
import { createSelector } from 'reselect';
import { dealStatuses } from '../../constants';
import { getCampaignById } from './campaigns';

export const getDealList = state => state.firestore.ordered.deals;

export const getActiveDeals = createSelector(
  [getDealList],
  dealList => {
    return filter(dealList, deal => deal.status === dealStatuses.ACTIVE);
  }
);

export const getActiveDealsCount = createSelector(
  [getActiveDeals],
  activeDeals => {
    return activeDeals.length;
  }
);

export const getCompleteDeals = createSelector(
  [getDealList],
  dealList => {
    return filter(dealList, deal => deal.status === dealStatuses.COMPLETE);
  }
);

export const getCompleteDealsCount = createSelector(
  [getCompleteDeals],
  completedDealsList => {
    return completedDealsList.length;
  }
);

export const getPendingContentApproval = createSelector(
  [getDealList],
  dealList => {
    return filter(
      dealList,
      deal => deal.status === dealStatuses.CONTENT_COMPLETE
    );
  }
);

export const getPendingContentApprovalCount = createSelector(
  [getPendingContentApproval],
  pendingList => {
    return pendingList.length;
  }
);

export const getPendingDeals = createSelector(
  [getDealList],
  dealList => {
    return filter(dealList, deal => deal.status === dealStatuses.PROPOSAL_SENT);
  }
);

export const getPendingDealsCount = createSelector(
  [getPendingDeals],
  pendingDeals => {
    return pendingDeals.length;
  }
);

export const getPendingMatchesForInfluencer = createSelector(
  [getDealList],
  dealList => {
    return filter(
      dealList,
      deal => deal.status === dealStatuses.OFFER && deal.initiator === 'company'
    );
  }
);

export const getPendingMatchesForInfluencerCount = createSelector(
  [getPendingMatchesForInfluencer],
  pendingMatches => {
    return pendingMatches.length;
  }
);

export const getPendingMatchesForCompany = createSelector(
  [getDealList],
  dealList => {
    return filter(
      dealList,
      deal =>
        deal.status === dealStatuses.OFFER && deal.initiator === 'influencer'
    );
  }
);

export const getPendingMatchesForCompanyCount = createSelector(
  [getPendingMatchesForCompany],
  pendingMatches => {
    return pendingMatches.length;
  }
);

export const getWaitingForEscrow = createSelector(
  [getDealList],
  dealList => {
    return filter(dealList, deal => deal.status === dealStatuses.DEAL_ACCEPTED);
  }
);

export const getWaitingForEscrowCount = createSelector(
  [getWaitingForEscrow],
  pendingEscrow => {
    return pendingEscrow.length;
  }
);

export const getCompanyMatchCount = createSelector(
  [getDealList],
  dealList => {
    return uniqBy(dealList, deal => deal.influencerId).length;
  }
);

export const getInfluencerMatchCount = createSelector(
  [getDealList],
  dealList => {
    return uniqBy(dealList, deal => deal.companyId).length;
  }
);

/********* Campaign Group Queries ************/

export const getDealsByCampaignId = campaignId => {
  return createSelector(
    [getDealList],
    dealList => {
      return filter(dealList, deal => deal.campaignId === campaignId);
    }
  );
};

export const getOffersByCampaignId = createSelector(
  [getDealsByCampaignId],
  dealList => {
    return filter(dealList, deal => deal.status === dealStatuses.OFFER);
  }
);

// export const getOffersByCampaignId = (state, campaignId) => {
//   return createSelector(
//   [getDealsByCampaignId(campaignId)],
//   dealList => {
//     return filter(dealList, deal => deal.status === dealStatuses.OFFER)
//   }
// )};

// export const getOffersCountByCampaignId = (state, campaignId) => {
//   return createSelector(
//   [getOffersByCampaignId(campaignId)],
//   deals => {
//     return deals.length;
//   }
// )};

export const getMatchedDealsByCampaignId = campaignId => {
  return createSelector(
    [getDealsByCampaignId(campaignId)],
    dealList => {
      return filter(dealList, deal => deal.status === dealStatuses.MATCHED);
    }
  );
};

export const getMatchedDealsCountByCampaignId = campaignId => {
  return createSelector(
    [getMatchedDealsByCampaignId(campaignId)],
    deals => {
      return deals.length;
    }
  );
};

export const getActiveDealsByCampaignId = campaignId => {
  return createSelector(
    [getDealsByCampaignId(campaignId)],
    dealList => {
      return filter(dealList, deal => deal.status === dealStatuses.ACTIVE);
    }
  );
};

export const getActiveDealsCountByCampaignId = campaignId => {
  return createSelector(
    [getActiveDealsByCampaignId(campaignId)],
    deals => {
      return deals.length;
    }
  );
};

export const getCompleteDealsByCampaignId = campaignId => {
  return createSelector(
    [getDealsByCampaignId(campaignId)],
    dealList => {
      return filter(dealList, deal => deal.status === dealStatuses.COMPLETE);
    }
  );
};

export const getCompleteDealsCountByCampaignId = campaignId => {
  return createSelector(
    [getCompleteDealsByCampaignId(campaignId)],
    deals => {
      return deals.length;
    }
  );
};

export const getProposedDealsByCampaignId = campaignId => {
  return createSelector(
    [getDealsByCampaignId(campaignId)],
    dealList => {
      return filter(
        dealList,
        deal =>
          deal.status === dealStatuses.PROPOSAL_SENT ||
          deal.status === dealStatuses.DEAL_ACCEPTED ||
          deal.status === dealStatuses.MATCHED ||
          deal.status === dealStatuses.OFFER
      );
    }
  );
};

export const getActiveandOrFutherByCampaignId = campaignId => {
  return createSelector(
    [getDealsByCampaignId(campaignId)],
    dealList => {
      return filter(
        dealList,
        deal =>
          deal.status === dealStatuses.ACTIVE ||
          deal.status === dealStatuses.CONTENT_COMPLETE ||
          deal.status === dealStatuses.COMPLETE
      );
    }
  );
};

export const getShortlistedDealsByCampaignId = campaignId => {
  return createSelector(
    [getDealsByCampaignId(campaignId)],
    dealList => {
      return filter(dealList, deal => deal.isShortlisted);
    }
  );
};

export const getMessagedDealsByCampaignId = campaignId => {
  return createSelector(
    [getDealsByCampaignId(campaignId)],
    dealList => {
      return filter(dealList, deal => deal.hasBeenMessaged);
    }
  );
};

export const getPendingEscrowDealsByCampaignId = campaignId => {
  return createSelector(
    [getDealsByCampaignId(campaignId)],
    dealList => {
      return filter(
        dealList,
        deal => deal.status === dealStatuses.DEAL_ACCEPTED
      );
    }
  );
};



// WEBPACK FOOTER //
// ./src/store/selectors/deals.js